import { Array, Boolean, Null, Number, Optional, Record, Static, String, Undefined } from 'runtypes';

import {
	IdoFieldContactPersonRunType,
	IdoFieldLastContactDateRunType,
	IdoFieldMultipleChoiceRunType,
	IdoFieldNextContactDateRunType,
	IdoFieldNextContactTimeCheckboxRunType,
	IdoFieldNextContactTimeRunType,
	IdoFieldSingleChoiceRunType,
	IdoFieldTextAreaRunType,
	IdoFieldValueFaxActionRunType,
	IdoFieldValueLinkActionRunType,
	IdoFieldValueMailActionRunType,
	IdoFieldValuePhoneActionRunType,
	IdoFieldValueRunType,
} from 'src/models/ido/fieldTypes';
import {
	ChangelogJSONTabFactoryRunType,
	DesktopModuleIdoPrintApplicationJSONTabFactoryRunType,
	DocumentJSONTabFactoryRunType,
	GenericJSONTabFactoryRunType,
	HistoryJSONTabFactoryRunType,
	IFrameTabFactoryRunType,
	MailAttachmentJSONTabFactoryRunType,
	MasterDataJSONTabFactoryRunType,
	RelationsJSONTabFactoryRunType,
} from 'src/models/ido/tabTypes';

const IdoWindowIdoRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	displayName: String,
	processId: String,
	title: String,
});

export const IdoWindowIdoInstanceRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	color: String,
	idoWindowIdos: Array(IdoWindowIdoRunType),
	processId: String,
	title: String,
});

export type IdoWindowIdoInstance = Static<typeof IdoWindowIdoInstanceRunType>;

const IdoWindowRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	idoWindowIdoInstances: Array(IdoWindowIdoInstanceRunType),
	processId: String,
});

export const IdoButtonRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	changeable: Boolean,
	idoCount: Number,
	idoWindow: IdoWindowRunType,
	processId: String,
	render: Boolean,
});

export type IdoButton = Static<typeof IdoButtonRunType>;

export const GetTheIdoButtonSearchResultsQueryReturnRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	idoDisplayName: String,
	idoId: Number,
	idoTableView: Record({}).Or(Null),
	idoTableViewAsHTML: String,
	idoTitle: String,
	instanceColor: String,
	instanceId: Number,
	instanceTitle: String,
	processId: String,
	relevance: Number,
});

export type GetTheIdoButtonSearchResultsQueryReturn = Static<typeof GetTheIdoButtonSearchResultsQueryReturnRunType>;

export const GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturnRunType = Record({
	callerName: String,
});

export type GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturn = Static<
	typeof GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturnRunType
>;

export const IdoContactPersonsRunType = Record({
	contactPersonId: Number,
	fields: Array(
		IdoFieldValueMailActionRunType.Or(IdoFieldValuePhoneActionRunType)
			.Or(IdoFieldValueFaxActionRunType)
			.Or(IdoFieldValueLinkActionRunType)
			.Or(IdoFieldValueRunType)
			.Or(IdoFieldNextContactDateRunType)
	),
});

export type IdoContactPersons = Static<typeof IdoContactPersonsRunType>;

const IdoJsonModelColumnsRunType = Record({
	contactPersons: Array(IdoContactPersonsRunType),
	contactPersonsFields: Array(
		IdoFieldValueMailActionRunType.Or(IdoFieldValuePhoneActionRunType)
			.Or(IdoFieldValueFaxActionRunType)
			.Or(IdoFieldValueLinkActionRunType)
			.Or(IdoFieldValueRunType)
			.Or(IdoFieldNextContactDateRunType)
	),
	leftColumn: Array(
		IdoFieldContactPersonRunType.Or(IdoFieldLastContactDateRunType)
			.Or(IdoFieldSingleChoiceRunType)
			.Or(IdoFieldMultipleChoiceRunType)
			.Or(IdoFieldNextContactDateRunType)
			.Or(IdoFieldNextContactTimeCheckboxRunType)
			.Or(IdoFieldNextContactTimeRunType)
			.Or(IdoFieldTextAreaRunType)
			.Or(IdoFieldValueMailActionRunType)
			.Or(IdoFieldValuePhoneActionRunType)
			.Or(IdoFieldValueFaxActionRunType)
			.Or(IdoFieldValueLinkActionRunType)
			.Or(IdoFieldValueRunType)
	),
	rightColumn: Array(
		IdoFieldContactPersonRunType.Or(IdoFieldLastContactDateRunType)
			.Or(IdoFieldSingleChoiceRunType)
			.Or(IdoFieldMultipleChoiceRunType)
			.Or(IdoFieldNextContactDateRunType)
			.Or(IdoFieldNextContactTimeCheckboxRunType)
			.Or(IdoFieldNextContactTimeRunType)
			.Or(IdoFieldTextAreaRunType)
			.Or(IdoFieldValueMailActionRunType)
			.Or(IdoFieldValuePhoneActionRunType)
			.Or(IdoFieldValueFaxActionRunType)
			.Or(IdoFieldValueLinkActionRunType)
			.Or(IdoFieldValueRunType)
	),
	tabConfiguration: Array(
		ChangelogJSONTabFactoryRunType.Or(DesktopModuleIdoPrintApplicationJSONTabFactoryRunType)
			.Or(DocumentJSONTabFactoryRunType)
			.Or(HistoryJSONTabFactoryRunType)
			.Or(IFrameTabFactoryRunType)
			.Or(MailAttachmentJSONTabFactoryRunType)
			.Or(MasterDataJSONTabFactoryRunType)
			.Or(RelationsJSONTabFactoryRunType)
			.Or(GenericJSONTabFactoryRunType)
	),
});

const IdoJsonModelAllowedActionsRunType = Record({
	delete: Boolean,
});

export const IdoJsonModelRunType = Record({
	allowedActions: IdoJsonModelAllowedActionsRunType,
	idoId: String,
	idoInstanceId: String,
	instanceColor: String,
	instanceTitle: String,
	lastKnownChangeTimestamp: String,
	masterDataExtJSTabTitle: String,
	model: IdoJsonModelColumnsRunType,
});

export type IdoJsonModel = Static<typeof IdoJsonModelRunType>;

export const IdoRecordRunType = Record({
	isAnDeletedIdo: Boolean,
	isAnFavoriteIdo: Optional(Boolean),
	jsonModel: Optional(IdoJsonModelRunType.Or(Null)),
});

export type IdoRecord = Static<typeof IdoRecordRunType>;

const IdoMasterDataFieldChangelogRunType = Record({
	fullUsername: String,
	timestamp: Number.Or(String),
});

export const IdoMasterDataFieldValueRunType = Record({
	changelog: Optional(IdoMasterDataFieldChangelogRunType.Or(Null).Or(Undefined)),
	description: String,
	id: Number.Or(String),
});

export type IdoMasterDataFieldValue = Static<typeof IdoMasterDataFieldValueRunType>;

export const IdoMasterDataFieldValuesRunType = Record({
	fields: Array(IdoMasterDataFieldValueRunType),
	lastKnownChangeTimestamp: Number.Or(String),
});

export type IdoMasterDataFieldValues = Static<typeof IdoMasterDataFieldValuesRunType>;

export const ReferencedIdoRunType = Record({
	idoId: Number,
	idoInstanceId: Number,
});

export type ReferencedIdo = Static<typeof ReferencedIdoRunType>;

const LinkedIdoValueRunType = Record({
	aggregateId: String,
	idoDisplayName: String,
	idoId: Number,
	idoInstanceId: Number,
	idoTableView: Record({}).Or(Null),
	idoTitle: String,
	instanceColor: String,
	instanceTitle: String,
});

export const LinkedIdoRunType = Record({
	label: String,
	value: LinkedIdoValueRunType,
});

export type LinkedIdo = Static<typeof LinkedIdoRunType>;

export const IdoRelationsForAnIdoRunType = Record({
	completeCount: Number,
	instanceColor: String,
	instanceId: Number,
	instanceName: String,
});

export type IdoRelationsForAnIdo = Static<typeof IdoRelationsForAnIdoRunType>;

export type IdoLinkingsParameter = {
	idoId: string;
	instanceId: string;
};
